/**
 * Angaben der Registerkarten „Überprüfen“ und „Ansicht“ sowie der
 * Backstage-Ansicht: Rechtschreibung, Sprache, Lesbarkeit, Fensteransicht,
 * Dokumenteigenschaften und Makros.
 *
 * Die Dokumenteigenschaften gibt es seit WordApi 1.3. Sprache und
 * Silbentrennung liefert WordApiDesktop 1.3, Rechtschreibfehler,
 * Lesbarkeitsstatistik, Fensteransicht und VBA-Projekt erst
 * WordApiDesktop 1.4. Jede Funktion liefert `null`, wenn ihr Satz fehlt.
 */

import { supportsWordApi, supportsWordApiDesktop, wordApi, wordApiDesktop } from "./capabilities";
import { isWordAvailable, WordUnavailableError } from "./document";

export interface Rechtschreibstand {
  rechtschreibfehler: number;
  grammatikfehler: number;
  /** Zeigt Word die Wellenlinien überhaupt an? */
  fehlerAngezeigt: boolean | null;
}

export async function readRechtschreibung(): Promise<Rechtschreibstand | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApiDesktop(wordApiDesktop.dokumentDetails)) return null;

  try {
    return await Word.run(async (context) => {
      const rechtschreibung = context.document.spellingErrors;
      rechtschreibung.load("items/text");
      const grammatik = context.document.grammaticalErrors;
      grammatik.load("items/text");
      context.document.load("areSpellingErrorsShown");
      await context.sync();
      return {
        rechtschreibfehler: rechtschreibung.items.length,
        grammatikfehler: grammatik.items.length,
        fehlerAngezeigt:
          typeof context.document.areSpellingErrorsShown === "boolean"
            ? context.document.areSpellingErrorsShown
            : null,
      };
    });
  } catch {
    return null;
  }
}

export interface AbsatzSprache {
  index: number;
  text: string;
  /** Sprachunabhängiger Name wie "German", "EnglishUS" – `null` bei gemischter Sprache. */
  sprache: string | null;
}

/** Sprache jedes Absatzes mit Inhalt. `null`, wenn WordApiDesktop 1.3 fehlt. */
export async function readSprachen(): Promise<AbsatzSprache[] | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApiDesktop(wordApiDesktop.sprache)) return null;

  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items/text");
      await context.sync();

      const bereiche = paragraphs.items.map((paragraph) => {
        if (!paragraph.text || paragraph.text.trim().length === 0) return null;
        const bereich = paragraph.getRange("Content");
        bereich.load("languageId");
        return bereich;
      });
      await context.sync();

      return bereiche.flatMap((bereich, index) =>
        bereich
          ? [
              {
                index,
                text: (paragraphs.items[index].text ?? "").trim(),
                sprache: typeof bereich.languageId === "string" ? bereich.languageId : null,
              },
            ]
          : [],
      );
    });
  } catch {
    return null;
  }
}

/** Gehört die Sprachkennung zu einer deutschen Variante? */
export function istDeutsch(sprache: string | null): boolean {
  return sprache !== null && /^(German|Deutsch)/i.test(sprache);
}

/** Gehört die Sprachkennung zu einer englischen Variante? */
export function istEnglisch(sprache: string | null): boolean {
  return sprache !== null && /^English/i.test(sprache);
}

export interface Lesbarkeitswert {
  name: string;
  wert: number;
}

/**
 * Lesbarkeitsstatistik („Überprüfen → Rechtschreibung und Grammatik“ →
 * Lesbarkeitsstatistik). Die Namen sind die der Word-Oberfläche und damit
 * sprachabhängig; die Reihenfolge ist fest: Wörter, Zeichen, Absätze, Sätze,
 * Sätze pro Absatz, Wörter pro Satz, Zeichen pro Wort, Passivsätze,
 * Lesbarkeitsindex nach Flesch, Flesch-Kincaid-Stufe.
 */
export async function readLesbarkeit(): Promise<Lesbarkeitswert[] | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApiDesktop(wordApiDesktop.dokumentDetails)) return null;

  try {
    return await Word.run(async (context) => {
      const statistik = context.document.readabilityStatistics;
      statistik.load("items/name,items/value");
      await context.sync();
      return statistik.items.map((eintrag) => ({
        name: typeof eintrag.name === "string" ? eintrag.name : "",
        wert: typeof eintrag.value === "number" ? eintrag.value : Number(eintrag.value),
      }));
    });
  } catch {
    return null;
  }
}

/** Position der Werte in der Lesbarkeitsstatistik (nullbasiert). */
export const LESBARKEIT = {
  woerter: 0,
  saetze: 3,
  woerterProSatz: 5,
  passivsaetze: 7,
  flesch: 8,
} as const;

export interface Fensteransicht {
  /** "Print", "Web", "Outline", "Reading", "Normal" (Entwurf) … */
  typ: string | null;
  navigationsbereich: boolean | null;
  formatierungszeichen: boolean | null;
}

export async function readAnsicht(): Promise<Fensteransicht | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApiDesktop(wordApiDesktop.dokumentDetails)) return null;

  try {
    return await Word.run(async (context) => {
      const fenster = context.document.activeWindow;
      fenster.load("isDocumentMapVisible");
      const ansicht = fenster.view;
      ansicht.load("type,areAllNonprintingCharactersDisplayed");
      await context.sync();
      return {
        typ: typeof ansicht.type === "string" ? ansicht.type : null,
        navigationsbereich:
          typeof fenster.isDocumentMapVisible === "boolean" ? fenster.isDocumentMapVisible : null,
        formatierungszeichen:
          typeof ansicht.areAllNonprintingCharactersDisplayed === "boolean"
            ? ansicht.areAllNonprintingCharactersDisplayed
            : null,
      };
    });
  } catch {
    return null;
  }
}

export interface Dokumenteigenschaften {
  title: string;
  subject: string;
  author: string;
  keywords: string;
  category: string;
  company: string;
  comments: string;
  /** Name der Vorlage, auf der das Dokument beruht, z. B. "Normal.dotm". */
  template: string;
}

export async function readEigenschaften(): Promise<Dokumenteigenschaften | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApi(wordApi.properties)) return null;

  try {
    return await Word.run(async (context) => {
      const p = context.document.properties;
      p.load("title,subject,author,keywords,category,company,comments,template");
      await context.sync();
      const text = (value: unknown) => (typeof value === "string" ? value.trim() : "");
      return {
        title: text(p.title),
        subject: text(p.subject),
        author: text(p.author),
        keywords: text(p.keywords),
        category: text(p.category),
        company: text(p.company),
        comments: text(p.comments),
        template: text(p.template),
      };
    });
  } catch {
    return null;
  }
}

/** Beruht das Dokument auf einer eigenen Vorlage statt auf Normal.dotm? */
export function hatEigeneVorlage(eigenschaften: Dokumenteigenschaften): boolean {
  const vorlage = eigenschaften.template.toLowerCase();
  return vorlage.length > 0 && !/^normal\.dot[mx]?$/.test(vorlage);
}

/**
 * Dateiname der geöffneten Datei, soweit Office ihn preisgibt. Bei einer noch
 * nie gespeicherten Datei leer.
 */
export function dateiAdresse(): string {
  try {
    return typeof Office !== "undefined" ? (Office.context?.document?.url ?? "") : "";
  } catch {
    return "";
  }
}

/** Dateiendung der geöffneten Datei in Kleinbuchstaben, z. B. "docm". */
export function dateiendung(): string | null {
  const adresse = dateiAdresse().split(/[?#]/)[0];
  const treffer = /\.([a-z0-9]{3,4})$/i.exec(adresse);
  return treffer ? treffer[1].toLowerCase() : null;
}

/** Enthält das Dokument ein VBA-Projekt? `null`, wenn WordApiDesktop 1.4 fehlt. */
export async function readHatMakros(): Promise<boolean | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApiDesktop(wordApiDesktop.dokumentDetails)) return null;

  try {
    return await Word.run(async (context) => {
      context.document.load("hasVbProject");
      await context.sync();
      return typeof context.document.hasVbProject === "boolean"
        ? context.document.hasVbProject
        : null;
    });
  } catch {
    return null;
  }
}
